/**
 * 수업일지 운동 기록(workoutRecord) 뷰어용 타입 · 정규화 · 세트 요약.
 *
 * 백엔드는 `post.scheduleReport.workoutRecord`에 전체 스냅샷을 그대로 내려주지만
 * 세트 요약("3세트 x 15~20kg x 15회")은 서버가 계산하지 않는다. 아래 로직은 백엔드
 * `rappo/src/utils/workoutRecord.js`의 normalizeWorkoutRecord / summarizeExerciseSets를
 * 그대로 포팅한 것이다. 규칙이 바뀌면 양쪽을 함께 맞춰야 한다.
 */

import { getExerciseThumbnailUrl } from './exerciseThumbnails';

/** contents[] 안의 운동 위치 마커 contentType */
export const WORKOUT_EXERCISE_MARKER = 'WORKOUT_EXERCISE';

export type WorkoutSet = {
    setIndex: number;
    weight?: number | null;
    weightUnit?: string; // V1: 'kg'
    reps?: number | null;
    duration?: number | null;
    durationUnit?: string; // 'sec' | 'min'
    distance?: number | null;
    distanceUnit?: string; // 'm' | 'km'
    calories?: number | null;
    note?: string;
};

export type WorkoutMedia = {
    contentType: 'VIDEO' | 'IMG';
    content: string;
    thumbnail?: string;
    duration?: number | null;
};

export type WorkoutExercise = {
    order: number;
    exerciseId?: string;
    exerciseName: string;
    category?: string;
    bodyParts?: string[];
    equipment?: string[];
    thumbnailUrl?: string;
    thumbnailSource?: Record<string, unknown>;
    isCustomExercise: boolean;
    exerciseInfo?: string;
    cue?: string;
    mediaContents?: WorkoutMedia[];
    sets: WorkoutSet[];
};

export type WorkoutRecord = {
    activityCategory?: string;
    durationMinutes?: number | null;
    summaryText?: string;
    closingMessage?: string;
    exercises: WorkoutExercise[];
};

/** '' | null | undefined -> null, 그 외 유한수만 통과 */
export const toNumberOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
};

const normalizeStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value.map((item) => String(item).trim()).filter(Boolean);
};

const trimmedString = (value: unknown): string | undefined => {
    if (value === null || value === undefined) return undefined;
    const str = String(value).trim();
    return str ? str : undefined;
};

/** 정수는 그대로, 소수는 뒤 0 제거 */
export const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return Number.isInteger(value) ? String(value) : String(value).replace(/\.0+$/, '');
};

/** null/undefined 제거 → dedupe → min~max (동일하면 단일값). 0은 유효값으로 보존 */
export const formatRange = (values: Array<number | null | undefined>): string => {
    const unique = Array.from(
        new Set(values.filter((v): v is number => v !== null && v !== undefined)),
    );
    if (unique.length === 0) return '';
    const min = Math.min(...unique);
    const max = Math.max(...unique);
    return min === max ? formatNumber(min) : `${formatNumber(min)}~${formatNumber(max)}`;
};

const durationLabelOf = (unit: string | undefined): string => {
    if (unit === 'min') return '분';
    if (unit === 'sec') return '초';
    return unit || '분';
};

/**
 * 운동 1개의 세트 요약 문자열.
 * 체크 순서(고정): 무게+횟수 → 횟수만 → 유산소(시간/거리/칼로리) → 값없음 세트수.
 * 무게만 있고 횟수가 없으면 요약에는 안 나타남(= `${n}세트`).
 * sets가 비면 '' (요약 라인 숨김).
 */
export const summarizeExerciseSets = (sets: WorkoutSet[] = []): string => {
    const validSets = Array.isArray(sets) ? sets : [];
    if (validSets.length === 0) return '';

    const setCount = validSets.length;
    const weightRange = formatRange(validSets.map((s) => toNumberOrNull(s?.weight)));
    const repsRange = formatRange(validSets.map((s) => toNumberOrNull(s?.reps)));
    const durationRange = formatRange(validSets.map((s) => toNumberOrNull(s?.duration)));
    const distanceRange = formatRange(validSets.map((s) => toNumberOrNull(s?.distance)));
    const calorieRange = formatRange(validSets.map((s) => toNumberOrNull(s?.calories)));

    const weightUnit = validSets.find((s) => s?.weightUnit)?.weightUnit || 'kg';
    const rawDurationUnit = validSets.find((s) => s?.durationUnit)?.durationUnit || '분';
    const distanceUnit = validSets.find((s) => s?.distanceUnit)?.distanceUnit || 'km';
    const durationLabel = durationLabelOf(rawDurationUnit);

    if (weightRange && repsRange) {
        return `${setCount}세트 x ${weightRange}${weightUnit} x ${repsRange}회`;
    }
    if (repsRange) {
        return `${setCount}세트 x ${repsRange}회`;
    }
    if (durationRange || distanceRange || calorieRange) {
        const cardio = [
            durationRange ? `${durationRange}${durationLabel}` : '',
            distanceRange ? `${distanceRange}${distanceUnit}` : '',
            calorieRange ? `${calorieRange}kcal` : '',
        ]
            .filter(Boolean)
            .join(' · ');
        return setCount === 1 ? cardio : `${setCount}세트 x ${cardio}`;
    }
    return `${setCount}세트`;
};

/**
 * 세트 1개의 상세 라인. "1세트 15kg x 15회" / "2세트 30분" 등.
 * 요약과 달리 무게만/유산소도 모두 노출(정보성). 값 없으면 세트번호만.
 */
export const formatSetDetail = (set: WorkoutSet, fallbackIndex: number): string => {
    const setIndex = toNumberOrNull(set?.setIndex) || fallbackIndex + 1;
    const weight = toNumberOrNull(set?.weight);
    const reps = toNumberOrNull(set?.reps);
    const duration = toNumberOrNull(set?.duration);
    const distance = toNumberOrNull(set?.distance);
    const calories = toNumberOrNull(set?.calories);

    const parts: string[] = [];
    if (weight !== null && reps !== null) {
        parts.push(`${formatNumber(weight)}${set?.weightUnit || 'kg'} x ${formatNumber(reps)}회`);
    } else if (reps !== null) {
        parts.push(`${formatNumber(reps)}회`);
    } else if (weight !== null) {
        parts.push(`${formatNumber(weight)}${set?.weightUnit || 'kg'}`);
    }
    if (duration !== null) parts.push(`${formatNumber(duration)}${durationLabelOf(set?.durationUnit)}`);
    if (distance !== null) parts.push(`${formatNumber(distance)}${set?.distanceUnit || 'km'}`);
    if (calories !== null) parts.push(`${formatNumber(calories)}kcal`);

    const note = trimmedString(set?.note);
    const body = parts.join(' · ');
    const label = `${setIndex}세트`;
    const detail = body ? `${label} ${body}` : label;
    return note ? `${detail} · ${note}` : detail;
};

/**
 * 스냅샷 thumbnailUrl 이 큐레이팅된(비 wger) 이미지인지 판정.
 * 앱은 raw wger URL 을 표시에 쓰지 않고 자체(번들/CDN) 이미지를 쓴다. 웹도 동일하게
 * wger URL 은 무시하고 CDN 운동 이미지를 우선한다. 강사가 직접 올린 커스텀 이미지나
 * isCurated 로 마킹된 것은 그대로 사용한다.
 */
const getCuratedThumbnailUrl = (
    exercise: { thumbnailUrl?: string; thumbnailSource?: Record<string, unknown> },
): string | undefined => {
    const url = exercise.thumbnailUrl?.trim();
    if (!url) return undefined;
    const source = (exercise.thumbnailSource || {}) as { provider?: unknown; isCurated?: unknown };
    const provider = String(source.provider || '').toLowerCase();
    const isWger = provider === 'wger' || /^https?:\/\/(?:www\.)?wger\.de(?:\/|$)/i.test(url);
    if (isWger && source.isCurated !== true) return undefined;
    return url;
};

/**
 * 운동 카드 썸네일 URL 해석 (앱 parity).
 * 1) 큐레이팅/직접 업로드 thumbnailUrl
 * 2) exerciseId(또는 운동명) → CDN 운동 이미지
 * 3) 마지막 수단: 스냅샷 raw thumbnailUrl (아이콘보다 나음)
 * 4) 없으면 undefined → 카드에서 기본 아이콘 폴백
 */
export const resolveExerciseThumbnail = (exercise: {
    thumbnailUrl?: string;
    exerciseId?: string;
    exerciseName?: string;
    thumbnailSource?: Record<string, unknown>;
}): string | undefined => {
    const curated = getCuratedThumbnailUrl(exercise);
    if (curated) return curated;
    const cdn = getExerciseThumbnailUrl(exercise.exerciseId, exercise.exerciseName);
    if (cdn) return cdn;
    return exercise.thumbnailUrl?.trim() || undefined;
};

const normalizeMediaContents = (mediaContents: unknown): WorkoutMedia[] => {
    if (!Array.isArray(mediaContents)) return [];
    return mediaContents
        .filter((m) => m && (m.contentType === 'VIDEO' || m.contentType === 'IMG'))
        .map((m) => ({
            contentType: m.contentType as 'VIDEO' | 'IMG',
            content: m.content ? String(m.content) : '',
            thumbnail: trimmedString(m.thumbnail),
            duration: toNumberOrNull(m.duration),
        }))
        .filter((m) => m.content);
};

const normalizeSet = (set: any, index: number): WorkoutSet => {
    const weight = toNumberOrNull(set?.weight);
    const reps = toNumberOrNull(set?.reps);
    const duration = toNumberOrNull(set?.duration);
    const distance = toNumberOrNull(set?.distance);
    const calories = toNumberOrNull(set?.calories);
    const normalized: WorkoutSet = { setIndex: toNumberOrNull(set?.setIndex) || index + 1 };

    if (weight !== null) {
        normalized.weight = weight;
        normalized.weightUnit = set?.weightUnit ? String(set.weightUnit) : 'kg';
    }
    if (reps !== null) normalized.reps = reps;
    if (duration !== null) {
        normalized.duration = duration;
        normalized.durationUnit = set?.durationUnit ? String(set.durationUnit) : 'min';
    }
    if (distance !== null) {
        normalized.distance = distance;
        normalized.distanceUnit = set?.distanceUnit ? String(set.distanceUnit) : 'km';
    }
    if (calories !== null) normalized.calories = calories;
    const note = trimmedString(set?.note);
    if (note) normalized.note = note;
    return normalized;
};

/**
 * 앱/레거시에서 온 raw workoutRecord를 방어적으로 정규화.
 * 운동명 없는 운동은 드롭. 전부 비면 undefined 반환(→ 뷰어 미표시, 기존 일지 유지).
 */
export const normalizeWorkoutRecord = (raw: any): WorkoutRecord | undefined => {
    if (!raw || typeof raw !== 'object') return undefined;

    const exercises: WorkoutExercise[] = Array.isArray(raw.exercises)
        ? raw.exercises
              .map((exercise: any, index: number): WorkoutExercise | null => {
                  const exerciseName = trimmedString(exercise?.exerciseName);
                  if (!exerciseName) return null;
                  const exerciseId = trimmedString(exercise?.exerciseId);
                  return {
                      order: toNumberOrNull(exercise?.order) || index + 1,
                      exerciseId,
                      exerciseName,
                      category: trimmedString(exercise?.category),
                      bodyParts: normalizeStringArray(exercise?.bodyParts),
                      equipment: normalizeStringArray(exercise?.equipment),
                      thumbnailUrl: trimmedString(exercise?.thumbnailUrl),
                      thumbnailSource:
                          exercise?.thumbnailSource && typeof exercise.thumbnailSource === 'object'
                              ? exercise.thumbnailSource
                              : undefined,
                      isCustomExercise: Boolean(exercise?.isCustomExercise) || !exerciseId,
                      exerciseInfo: trimmedString(exercise?.exerciseInfo),
                      cue: trimmedString(exercise?.cue),
                      mediaContents: normalizeMediaContents(exercise?.mediaContents),
                      sets: Array.isArray(exercise?.sets)
                          ? exercise.sets.map((s: any, i: number) => normalizeSet(s, i))
                          : [],
                  };
              })
              .filter((e: WorkoutExercise | null): e is WorkoutExercise => e !== null)
        : [];

    const durationMinutes = toNumberOrNull(raw.durationMinutes);
    const normalized: WorkoutRecord = {
        activityCategory: trimmedString(raw.activityCategory),
        durationMinutes: durationMinutes && durationMinutes > 0 ? durationMinutes : null,
        summaryText: trimmedString(raw.summaryText),
        closingMessage: trimmedString(raw.closingMessage),
        exercises,
    };

    if (
        !normalized.summaryText &&
        !normalized.closingMessage &&
        (normalized.durationMinutes === null || normalized.durationMinutes === undefined) &&
        normalized.exercises.length === 0
    ) {
        return undefined;
    }
    return normalized;
};
